import { useIdleTimer } from 'react-idle-timer';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { requestApi } from 'api/Api';

import { isKioskDevice } from 'lib/helpers/kiosk';

import Modal from 'components/shared/Modal/Modal';
import NotFound from 'components/practice/Dashboard/components/NotFound';

// Patient screens a kiosk hands off to that live outside the /kiosk tree: instant-action
// links, the check-in wizard, and the public invoice/receipt page. A kiosk check-in starts
// as /ia/<link> and then runs entirely on /checkin/*, and pushed payments land on
// /invoice/<token>, so without these the timer stops at the handoff.
const HANDOFF_PREFIXES = ['/ia/', '/instant-packet', '/checkin', '/invoice/'];

const isHandoffScreen = (pathname) =>
  HANDOFF_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// Where to send the patient back to when a shared/kiosk device goes idle.
// Each destination is itself excluded from the in-scope check below, so there's
// no redirect loop once we land on it.
const resolveIdleReset = (pathname) => {
  if (pathname.startsWith('/mobile/')) return '/mobile/idle';
  if (isHandoffScreen(pathname)) return '/kiosk/idle';
  return '/kiosk';
};

// Single, app-wide guard (mounted once in App.jsx). It decides whether the
// current screen belongs to a shared/kiosk device that must auto-logout on
// inactivity, so individual screens never have to opt in.
const AutoIdleWarper = () => {
  const { pathname } = useLocation();

  // `myriadIsKiosk` is set by KioskContextProvider on a physical kiosk and cleared on a
  // mobile/tablet kiosk (which is covered by its own /mobile/ screens). It is never set on
  // a patient's personal device, so personal instant links, own-device check-in and a
  // patient paying their own invoice link all stay excluded.
  const inScope =
    pathname.startsWith('/kiosk/') ||
    pathname.startsWith('/mobile/') ||
    (isKioskDevice() && isHandoffScreen(pathname));

  // Never run on the attract/idle/destination screens (prevents redirect loops).
  if (!inScope || pathname === '/kiosk' || pathname.includes('idle')) return null;

  return <AutoIdle resetTo={resolveIdleReset(pathname)} />;
};

const AutoIdle = ({ resetTo = '/kiosk' }) => {
  const { pathname } = useLocation();

  const [remaining, setRemaining] = useState(60);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const { reset } = useIdleTimer({
    timeout: 60 * 1000,
    onIdle: () => {
      setShowModal(true);
      requestApi({
        url: '/api/kiosk/idle_request',
        params: { status: 'idle', url: pathname },
        navigate
      });
      setRemaining(60);
    },
    onActive: () => {
      setShowModal(false);
      if (showModal) {
        requestApi({
          url: '/api/kiosk/idle_request',
          params: { status: 'active', url: pathname },
          navigate
        });
      }
      reset();
    },
    onAction: reset,
    debounce: 500,
    events: ['keydown', 'mousedown', 'touchstart', 'touchmove', 'MSPointerDown', 'MSPointerMove']
  });

  useEffect(() => {
    let interval;
    if (showModal) {
      interval = setInterval(() => {
        setRemaining((prevRemaining) => {
          if (prevRemaining <= 1) {
            clearInterval(interval);
            setRemaining(60);
            reset();
            navigate(resetTo);
            return 0;
          }
          return prevRemaining - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [showModal]);

  return (
    <Modal
      handleOpen={showModal && remaining <= 30}
      disableHeader={true}
      shouldCloseOnOverlayClick={false}
      overlayClassName="z-[300]"
      isFooter={false}
      isCentered>
      <div className="my-10">
        <NotFound
          icon="illustration-automatization"
          title="Are you still there?"
          description={`You will be logged out in ${Math.floor(remaining / 60)}:${
            remaining % 60 < 10 ? `0${remaining % 60}` : remaining % 60
          } minutes due to inactivity.`}
          className="pt-0"
        />
      </div>
    </Modal>
  );
};
export default AutoIdleWarper;
